import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { debounceTime, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SearchDto, PopularSearch } from '../../../../dto';
import { Pagination } from '../../../../core';
import { SearchService } from '../../../../services/search.service';
import { BaseComponent } from '../../../../core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

@Component({
  selector: 'app-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss'],
})
export class SearchModalComponent extends BaseComponent implements OnInit, OnDestroy {
  SEARCH_MODE: any = Object.freeze({
    RECENT: 'recent',
    TREND: 'trend',
  });

  SEARCH_TYPE = Object.freeze({
    BRAND: 'BRAND',
    PRODUCT: 'PRODUCT',
    SEARCH: 'SEARCH',
  });

  @ViewChild('Keyword', { read: ElementRef, static: false })
  Keyword: ElementRef;

  // searchTerm: string;
  isShowSearchModal = false;
  isSearching = false;
  searchMode = this.SEARCH_MODE.RECENT;
  isSearchingLists: SearchDto[];
  brandsType: SearchDto[];
  productType: SearchDto[];

  searchForm: FormGroup;
  recentSearches: Array<string> = [];
  pagination: Pagination = new Pagination();

  popularSearches: PopularSearch[];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private searchService: SearchService,
    private gtmService: GoogleTagManagerService
  ) {
    super();
  }

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      keyword: ['', [Validators.required]],
    });
    this.searchForm
      .get('keyword')
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((keyword) => {
        this.isSearching = (keyword && keyword !== '') || (keyword && keyword.length > 2);
        if (keyword) {
          this.searchService.getSearch(keyword).subscribe((searchDto) => {
            this.isSearchingLists = searchDto;

            this.brandsType = this.isSearchingLists.filter((list) => list.searchType.includes('BRAND'));
            this.productType = this.isSearchingLists.filter((list) => list.searchType.includes('PRODUCT'));

            this.sendKeywordToGA(keyword, this.brandsType, this.productType);
          });
        }
      });

    localStorage.getItem('search')
      ? (this.recentSearches = JSON.parse(localStorage.getItem('search')))
      : localStorage.setItem('search', JSON.stringify([]));

    this.searchService.getPopularSearch().subscribe((popularSearches) => {
      this.popularSearches = popularSearches;
    });
  }

  initModalMaximized(dialog: Dialog) {
    setTimeout(() => {
      dialog.maximize();
    }, 0);
  }

  // updateSearch(e) {
  //   this.searchTerm = e.target.value;
  // }

  openSearchModal() {
    this.isShowSearchModal = true;
    this.gtmService.pushTag({
      event: 'gaEvent',
      category: 'search',
      action: 'open search bar',
      label: 'open search bar',
    });
    setTimeout(() => {
      this.Keyword.nativeElement.focus();
    }, 500);
  }

  onClearSearch() {
    this.searchForm.get('keyword').setValue(null);
    this.searchMode = this.SEARCH_MODE.RECENT;
  }

  onSearch() {
    const keyword = this.searchForm.get('keyword').value;
    const isInclude = this.recentSearches.includes(keyword);
    if (keyword) {
      this.router.navigate([`/search`], { queryParams: { q: keyword } });
      this.isShowSearchModal = false;
      if (this.recentSearches.length < 10 && !isInclude) {
        this.recentSearches.unshift(keyword);
      } else if (!isInclude) {
        this.recentSearches.pop();
        this.recentSearches.unshift(keyword);
      }

      localStorage.setItem('search', JSON.stringify(this.recentSearches));
    } else {
      const dialog = this._openDialog({
        message: this.i18n.t('DIALOG.SEARCH.REQUIRED'),
        close: () => {
          dialog.close();
        },
      });
    }
  }

  onClickKeyword(keyword: string, isTrend?: boolean) {
    this.router.navigate([`/search`], { queryParams: { q: keyword } });
    this.isShowSearchModal = false;
    if (isTrend) {
      this.gtmService.pushTag({
        event: 'gaEvent',
        category: 'search',
        action: 'Click Search Trend Keyword',
        label: `Click Search Trend Keyword : ${keyword}`,
      });
    }
  }

  onCloseSearchModal(searchType?: string, id?: string | number) {
    this.isSearchingLists = [];
    this.isShowSearchModal = false;
    this.searchForm.get('keyword').setValue(null);

    if (searchType && id) {
      switch (searchType) {
        case this.SEARCH_TYPE.BRAND:
          this.router.navigate([`/brand`, id]);
          break;
        case this.SEARCH_TYPE.PRODUCT:
          this.searchForm.get('keyword').setValue('');
          this.router.navigate([`/product`, id], {
            state: { dataList: 'Preview Product' },
          });
          break;
      }
    }
  }

  removeItems(event: Event, i: number) {
    this.recentSearches.splice(i, 1);
    localStorage.setItem('search', JSON.stringify(this.recentSearches));
  }

  clearAllItems() {
    const dialog = this._openDialog({
      message: this.i18n.t('DIALOG.SEARCH.DELETE'),
      close: () => {
        localStorage.removeItem('search');
        this.recentSearches = [];
        dialog.close();
      },
      cancel: () => {
        dialog.close();
      },
    });
  }

  sendKeywordToGA(keyword: string, brandsType: SearchDto[], productType: SearchDto[]) {
    let action: string;
    if (brandsType.length === 0 && productType.length === 0) {
      action = 'brand-product-not-found';
    } else if (brandsType.length !== 0 && productType.length === 0) {
      action = 'brand-only-found';
    } else if (brandsType.length === 0 && productType.length !== 0) {
      action = 'product-only-found';
    } else {
      action = 'brand-product-found';
    }

    this.gtmService.pushTag({
      event: 'gaEvent',
      category: 'search-result',
      action,
      label: keyword,
    });
  }
}
